<script lang="ts">
  import { createEventDispatcher, onMount, onDestroy } from "svelte";
  import Sortable from "sortablejs";

  export let items: any[] = [];
  export let options: Partial<Sortable.Options> = {};
  export let handleClass: string = "drag-handle";
  export let ghostClass: string = "sortable-ghost";
  export let chosenClass: string = "sortable-chosen";
  export let dragClass: string = "sortable-drag";
  export let animation: number = 150;
  export let disabled: boolean = false;
  export let containerClass: string = "";
  export let idField: string = "id";
  export let generateId: boolean = false;
  export let autoNotify: boolean = true;
  export let notifyDelay: number = 0;

  let containerElement: HTMLElement;
  let sortableInstance: Sortable;
  const dispatch = createEventDispatcher();
  
  // Ensure all items have unique IDs if generateId is true
  $: if (generateId && items && items.length > 0) {
    items = items.map(item => {
      if (!item[idField]) {
        return {
          ...item,
          [idField]: `item-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
        };
      }
      return item;
    });
  }

  const defaultOptions: Partial<Sortable.Options> = {
    animation,
    ghostClass,
    chosenClass,
    dragClass,
    handle: `.${handleClass}`,
    disabled,
    onStart: (evt) => {
      dispatch('start', { 
        oldIndex: evt.oldIndex, 
        newIndex: evt.newIndex,
        item: evt.item
      });
    },
    onEnd: (evt) => {
      // Only reorder if indices are valid
      const oldIndex = typeof evt.oldIndex === "number" ? evt.oldIndex : 0;
      const newIndex = typeof evt.newIndex === "number" ? evt.newIndex : 0;
      
      if (oldIndex !== newIndex && 
          oldIndex >= 0 && oldIndex < items.length && 
          newIndex >= 0 && newIndex < items.length) {
        
        let itemsArray = [...items].filter(item => item !== null && item !== undefined);
        const movedItem = itemsArray.splice(oldIndex, 1)[0];
        itemsArray.splice(newIndex, 0, movedItem);
        
        items = itemsArray;
        
        const itemElement = evt.item;
        itemElement.classList.add("just-dropped");
        
        setTimeout(() => {
          itemElement.classList.remove("just-dropped");
        }, 1000);
        
        // Dispatch the update event with the new items array
        dispatch('update', { 
          items: itemsArray,
          oldIndex,
          newIndex,
          item: evt.item
        });
        
        // If autoNotify is enabled, dispatch the keyup
        if (autoNotify) {
          setTimeout(() => {
            dispatch('keyup');
          }, notifyDelay);
        }
      }
    }
  };

  function initSortable() {
    if (containerElement) {
      const sortableOptions = { ...defaultOptions, ...options };
      sortableInstance = Sortable.create(containerElement, sortableOptions);
    }
  }

  function destroySortable() {
    if (sortableInstance) {
      sortableInstance.destroy();
    }
  }

  onMount(initSortable);
  onDestroy(destroySortable);

  $: if (sortableInstance && options) {
    destroySortable();
    initSortable();
  }

  $: if (sortableInstance && sortableInstance.option) {
    sortableInstance.option('disabled', disabled);
  }
</script>

<div bind:this={containerElement} class="sortable-container {containerClass}">
  <slot />
</div>

<style>
  .sortable-container {
    width: 100%;
  }
</style>
